/**
 * Onboarding state, owned by the server.
 *
 * `workspace_onboarding` (0003) is one row per company: `completed_at` is the
 * one fact that matters most — once it is set, the guided setup is never
 * forced on this company again, however empty its book later becomes. That
 * distinction is deliberate and load-bearing: Fresh Start (0027) clears the
 * book but is *catalogued to keep* `workspace_onboarding`, specifically so
 * that resetting a mature company's book never looks, to this screen, like a
 * brand-new company that has not finished setting up. An empty book plus
 * "never completed onboarding" is a new company; an empty book plus
 * "completed onboarding, once" is a company that just ran Fresh Start.
 *
 * Nothing here decides *whether* the mandatory steps are actually done — that
 * is real state (a connected, synced data source), read independently by
 * `useSetupStatus`. This hook only remembers, durably and per company, that
 * the moment of completion already happened once, and lets whoever reaches
 * it — any member; this is progress-tracking, not a permission — record a
 * step reached or skipped.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface OnboardingState {
    step: string;
    completedSteps: string[];
    skippedSteps: string[];
    completedAt: string | null;
}

interface Row {
    step: string;
    completed_steps: string[] | null;
    skipped_steps: string[] | null;
    completed_at: string | null;
}

const DEFAULT_STATE: OnboardingState = { step: 'company', completedSteps: [], skippedSteps: [], completedAt: null };

export function useOnboarding(workspaceId: string) {
    const [state, setState] = useState<OnboardingState>(DEFAULT_STATE);
    const [loading, setLoading] = useState(true);
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        const { data } = await supabase()
            .from('workspace_onboarding').select('step, completed_steps, skipped_steps, completed_at')
            .eq('workspace_id', workspaceId).maybeSingle();
        if (issuedFor.current !== workspaceId) return;
        const row = data as Row | null;
        setState(row ? {
            step: row.step, completedSteps: row.completed_steps ?? [],
            skippedSteps: row.skipped_steps ?? [], completedAt: row.completed_at,
        } : DEFAULT_STATE);
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);

    /**
     * The mandatory setup is done for the first time. Idempotent (an update
     * with no matching row, or one already marked, changes nothing that
     * matters) and safe to call speculatively — callers do, every time the
     * condition holds, until it is reflected back from the server.
     */
    const markComplete = useCallback(async () => {
        if (state.completedAt) return;
        const now = new Date().toISOString();
        const { error } = await supabase()
            .from('workspace_onboarding').update({ completed_at: now, step: 'done' })
            .eq('workspace_id', workspaceId).is('completed_at', null);
        if (!error) setState(s => ({ ...s, completedAt: s.completedAt ?? now, step: 'done' }));
    }, [workspaceId, state.completedAt]);

    /** A step a person visited or explicitly moved past — resumable, not a gate. */
    const recordStep = useCallback(async (step: string) => {
        setState(s => ({ ...s, step }));
        await supabase().from('workspace_onboarding').update({ step }).eq('workspace_id', workspaceId);
    }, [workspaceId]);

    const skipStep = useCallback(async (step: string) => {
        setState(s => ({ ...s, skippedSteps: s.skippedSteps.includes(step) ? s.skippedSteps : [...s.skippedSteps, step] }));
        const { data } = await supabase().from('workspace_onboarding').select('skipped_steps').eq('workspace_id', workspaceId).maybeSingle();
        const current = ((data as { skipped_steps: string[] | null } | null)?.skipped_steps) ?? [];
        if (current.includes(step)) return;
        await supabase().from('workspace_onboarding').update({ skipped_steps: [...current, step] }).eq('workspace_id', workspaceId);
    }, [workspaceId]);

    return { ...state, loading, markComplete, recordStep, skipStep, reload: load };
}
